import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { pagesAPI } from '../services/api';
import { useSEO } from '../hooks/useSEO';

/**
 * Public renderer for admin-editable static pages stored in the `pages`
 * collection. Loads by `slug` and displays the HTML content with the same
 * FAQ-page typography.
 *
 * Content is admin-authored HTML — never sourced from end-user input — so
 * `dangerouslySetInnerHTML` is safe here.
 */
const PublicEditablePage = ({ slug, seo, fallbackTitle }) => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useSEO(seo);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await pagesAPI.get(slug);
        if (!cancelled) setPage(d);
      } catch {
        if (!cancelled) setPage(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <h1
            className="text-4xl sm:text-5xl font-bold text-gray-900 mb-2"
            data-testid={`${slug}-heading`}
          >
            {page?.title || fallbackTitle}
          </h1>
          <p className="text-gray-500 mb-8 text-sm">BlueSpark Australia</p>

          <Card>
            <CardContent className="p-6 md:p-10">
              {loading ? (
                <p className="text-gray-500">Loading…</p>
              ) : !page ? (
                <p className="text-red-600">
                  Content could not be loaded. Please try again shortly.
                </p>
              ) : (
                <div
                  data-testid={`${slug}-content`}
                  className="terms-content text-gray-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: page.content_html || '' }}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />

      <style>{`
        .terms-content h2 {
          font-size: 1.375rem;
          font-weight: 700;
          color: #0f172a;
          margin: 2rem 0 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid #e2e8f0;
        }
        .terms-content h2:first-of-type {
          border-top: none;
          padding-top: 0;
          margin-top: 1rem;
        }
        .terms-content p { margin: 0 0 1rem; }
        .terms-content ul { margin: 0 0 1rem 1.25rem; list-style: disc; }
        .terms-content li { margin-bottom: 0.5rem; }
        .terms-content strong { color: #0f172a; font-weight: 700; }
        .terms-content .acl-notice {
          background: #eff6ff;
          border-left: 4px solid #1d4ed8;
          padding: 1rem 1.25rem;
          margin: 1.25rem 0;
          border-radius: 0 0.375rem 0.375rem 0;
          color: #0f172a;
        }
        .terms-content a { color: #1d4ed8; text-decoration: underline; }
      `}</style>
    </div>
  );
};

export default PublicEditablePage;
